import ufeApi from 'services/api/ufeApi';

/**
 * Credential-lookup API service (SIM-2 / ACS-4126, SUB-TASK-2a).
 *
 * POSTs the normalized credential to the Backend Auth OI-4
 * `authenticationMethods` endpoint via the repo canonical ufeApi.makeRequest
 * client (never raw fetch). The concrete contract is published in
 * 2026/sign-in-modernization-ufe/artifacts/03-architecture/target-state/
 * api-contracts/auth-service-apis.md §1 and this service mirrors the sibling
 * auth POST precedent services/api/authentication/mfaVerification.js
 * (same `/gway/v1/dotcom/auth/v1/...` path prefix, same header set, same
 * errors[]/errorCode reject handling).
 *
 * `credentialType` ('EMAIL' | 'PHONE') is inferred on the FE and passed in the
 * body. Kasada `x-seph-kv` is interceptor-injected at the makeRequest level, so
 * it is NOT set here. The credential is never logged (BR-021).
 *
 * ufeApi.makeRequest resolves every non-network response and annotates it with a
 * transport `responseStatus`. This service returns the DOMAIN payload only
 * (transport field stripped) so the hook branches on contract fields:
 *   - single account: { securityToken, availableAuthMethods, phoneLast4Digits?,
 *     maskedEmail }
 *   - disambiguation:  { requiresEmailConfirmation: true }
 * On the backend errors[] / errorCode envelope, or a 5xx, the promise rejects so
 * the hook surfaces a server/lookup error. No auto-retry is added here.
 *
 * @param {string} credential - Email address or phone number submitted by customer
 * @param {string} credentialType - 'EMAIL' or 'PHONE' (inferred on FE)
 * @returns {Promise} - Resolves with the domain payload, rejects with error details
 */
function getCredentialLookup(credential, credentialType) {
    return ufeApi
        .makeRequest('/gway/v1/dotcom/auth/v1/authenticationMethods', {
            method: 'POST',
            body: JSON.stringify({ credential, credentialType }),
            headers: {
                'Content-type': 'application/json',
                'x-requested-source': 'web',
                'x-api-version': 1
            }
        })
        .then(response => {
            if (response?.errors) {
                return Promise.reject(response.errors[0]);
            }

            if (response?.errorCode) {
                return Promise.reject(response);
            }

            const { responseStatus, ...payload } = response || {};

            if (responseStatus >= 500) {
                return Promise.reject(response);
            }

            return payload;
        });
}

export default getCredentialLookup;
